import { addMinutes, format, roundToNearestMinutes, startOfDay } from 'date-fns';
import { formatInTimeZone, fromZonedTime, getTimezoneOffset, toZonedTime } from 'date-fns-tz';
import type { TFunction } from 'i18next';

import { BroadcastAction } from '@/shared/types/broadcast';

/** Backend scheduling horizon: 30 seconds to 14 days from now. */
export const MIN_SCHEDULE_DELAY_MS = 30 * 1000;
export const MAX_SCHEDULE_DELAY_MS = 14 * 24 * 60 * 60 * 1000;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Next half-hour boundary as a date + "HH:mm" pair — the default pick, and the
 * first entry the time dropdown offers for today. Returned as a pair so a late
 * evening `now` (23:47 → 00:00) rolls the date to tomorrow instead of defaulting
 * to a time that already passed today.
 */
export const getNextHalfHourSchedule = (now: Date = new Date()): { date: Date; time: string } => {
  // The +1 minute keeps the default past the 30s minimum at hh:29:4x / hh:59:4x.
  const next = roundToNearestMinutes(addMinutes(now, 1), { nearestTo: 30, roundingMethod: 'ceil' });
  return { date: next, time: format(next, 'HH:mm') };
};

/**
 * The calendar date it currently is in `timezone`, as a floating date (midnight
 * in runtime-local terms) so it can be compared with, and fed back into, the
 * picker's `date` — which `buildScheduledAt` reads as a bare `yyyy-MM-dd`.
 *
 * The picked day is interpreted in the *selected* zone, so "today" has to be
 * too: at 12:00 in Pacific/Midway it is already tomorrow in Pacific/Kiritimati,
 * and every slot on the runtime-local date is unschedulable there.
 */
export const getZonedToday = (timezone: string, now: Date = new Date()): Date =>
  startOfDay(toZonedTime(now, timezone));

/**
 * Combines a calendar date, an HH:mm time, and an IANA timezone into the
 * epoch-milliseconds fire time the backend expects.
 */
export const buildScheduledAt = (date: Date, time: string, timezone: string): number =>
  fromZonedTime(`${format(date, 'yyyy-MM-dd')}T${time}:00.000`, timezone).getTime();

export const isValidScheduleDelay = (scheduledAt: number, now: Date = new Date()): boolean => {
  const delay = scheduledAt - now.getTime();
  return delay >= MIN_SCHEDULE_DELAY_MS && delay <= MAX_SCHEDULE_DELAY_MS;
};

/** Whole days, hours, and minutes until the fire time, for the helper text. */
export const getScheduleDelayParts = (
  scheduledAt: number,
  now: Date = new Date()
): { days: number; hours: number; minutes: number } => {
  const delay = Math.max(0, scheduledAt - now.getTime());
  return {
    days: Math.floor(delay / DAY_MS),
    hours: Math.floor((delay % DAY_MS) / HOUR_MS),
    minutes: Math.floor((delay % HOUR_MS) / MINUTE_MS),
  };
};

// Cache one Intl.ListFormat per language — construction resolves locale data.
const listFormatCache = new Map<string, Intl.ListFormat>();
const getConjunctionListFormat = (locale: string): Intl.ListFormat => {
  const cached = listFormatCache.get(locale);
  if (cached) {
    return cached;
  }
  const formatter = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' });
  listFormatCache.set(locale, formatter);
  return formatter;
};

/**
 * Localized "time from now" for the helper: non-zero units joined per locale;
 * a valid sub-minute pick collapses to "less than a minute".
 */
export const formatScheduleDelay = (
  scheduledAt: number,
  t: TFunction,
  locale: string,
  now: Date = new Date()
): string => {
  const { days, hours, minutes } = getScheduleDelayParts(scheduledAt, now);
  const parts: string[] = [];
  if (days > 0) {
    parts.push(t('scheduled_messages.days', { count: days }));
  }
  if (hours > 0) {
    parts.push(t('scheduled_messages.hours', { count: hours }));
  }
  if (minutes > 0) {
    parts.push(t('scheduled_messages.minutes', { count: minutes }));
  }
  if (parts.length === 0) {
    return t('scheduled_messages.less_than_a_minute');
  }
  return getConjunctionListFormat(locale).format(parts);
};

/** ms offset → "UTC±HH:MM" (zero-padded, minutes-safe for half-hour zones). */
const formatUtcOffset = (offsetMs: number): string => {
  const sign = offsetMs < 0 ? '-' : '+';
  const totalMinutes = Math.abs(offsetMs) / MINUTE_MS;
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
  const minutes = String(Math.round(totalMinutes % 60)).padStart(2, '0');
  return `UTC${sign}${hours}:${minutes}`;
};

/** e.g. "Fri, May 22, 6:23 PM (UTC+02:00)" — viewer-local time + offset, for cards/toasts. */
export const formatScheduledAt = (scheduledAt: number): string => {
  const date = new Date(scheduledAt);
  const offset = formatUtcOffset(-date.getTimezoneOffset() * MINUTE_MS);
  return `${format(date, 'EEE, MMM d, h:mm a')} (${offset})`;
};

/**
 * Body text with positional `{{n}}` placeholders substituted from the
 * component's parameters. Unresolved placeholders are left as-is.
 */
export const getTemplateBodyPreview = (template: BroadcastAction): string => {
  const body = template?.components?.find((component) => component.type === 'body');
  if (!body?.text) {
    return '';
  }
  const parameters = body.parameters ?? [];
  return body.text.replace(
    /\{\{(\d+)\}\}/g,
    (match, index) => parameters[Number(index) - 1]?.text ?? match
  );
};

export interface TimeOption {
  /** 24-hour "HH:mm" — the value fed to `buildScheduledAt`. */
  value: string;
  /** Locale-aware display time ("2:30 PM" in en, "14:30" in es/pt-br). */
  label: string;
}

// Cache one Intl.DateTimeFormat per locale — construction resolves locale data.
const timeLabelFormatCache = new Map<string, Intl.DateTimeFormat>();
const getTimeLabelFormat = (locale: string): Intl.DateTimeFormat => {
  const cached = timeLabelFormatCache.get(locale);
  if (cached) {
    return cached;
  }
  const formatter = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' });
  timeLabelFormatCache.set(locale, formatter);
  return formatter;
};

/** Time options at a fixed interval for the time dropdown, as value/label pairs. */
export const getTimeOptions = (intervalMinutes = 30, locale = 'en'): TimeOption[] => {
  const timeFormat = getTimeLabelFormat(locale);
  const options: TimeOption[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += intervalMinutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    options.push({
      value: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
      // Newer ICU builds separate "2:30 PM" with a narrow no-break space —
      // normalize so labels are byte-stable across node versions.
      label: timeFormat.format(new Date(2000, 0, 1, h, m)).replace(/\u202f/g, ' '),
    });
  }
  return options;
};

// Cache one Intl.DateTimeFormat per locale — construction resolves locale data.
const dateLabelFormatCache = new Map<string, Intl.DateTimeFormat>();

/** Locale-aware date for the date trigger: "May 22, 2026" / "22 may 2026". */
export const formatDateLabel = (date: Date, locale = 'en'): string => {
  let formatter = dateLabelFormatCache.get(locale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    dateLabelFormatCache.set(locale, formatter);
  }
  return formatter.format(date);
};

/**
 * Time options for the dropdown with the unschedulable ones dropped: on today's
 * date every slot at or before now disappears (at 2:12 the list starts at 2:30),
 * and on the last allowed day the slots past the 14-day horizon disappear too.
 * Wall times inside a DST spring-forward gap (02:00/02:30 on the transition
 * day) don't exist — `fromZonedTime` maps them to a shifted instant that would
 * fire at a time the picker never displayed — so the round-trip check drops
 * them as well. Returns an empty list when the selected day has no schedulable
 * slot left.
 */
export const getAvailableTimeOptions = (
  date: Date,
  timezone: string,
  now: Date = new Date(),
  intervalMinutes = 30,
  locale?: string
): TimeOption[] =>
  getTimeOptions(intervalMinutes, locale).filter((option) => {
    const scheduledAt = buildScheduledAt(date, option.value, timezone);
    return (
      isValidScheduleDelay(scheduledAt, now) &&
      formatInTimeZone(scheduledAt, timezone, 'HH:mm') === option.value
    );
  });

/**
 * Offset + city display for an IANA timezone, DST-aware via a reference date.
 * 'Europe/Athens' → 'UTC+03:00 Athens'; 'America/Argentina/Buenos_Aires' →
 * 'UTC-03:00 Buenos Aires'. Pass the scheduled day as `referenceDate` so the
 * offset reflects DST for that day, not for "now".
 */
export const getTimezoneLabel = (timezone: string, referenceDate: Date = new Date()): string => {
  const city = timezone.split('/').pop()?.replace(/_/g, ' ') ?? timezone;
  return `${formatUtcOffset(getTimezoneOffset(timezone, referenceDate))} ${city}`;
};
